import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormResetField,
  UseFormSetValue,
} from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import React, { FC } from 'react';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

import { IItem } from '../../../types/IItem';
import { IServiceItem } from '../../../types/IServiceItem';
import './styles.css';

export const CatalogServices: FC<{
  servicesFields: string[];
  setServicesFields: React.Dispatch<React.SetStateAction<string[]>>;
  setValue: UseFormSetValue<IItem>;
  removeField: (val: number) => void;
  getValues: UseFormGetValues<IItem>;
  control: Control<IItem>;
  resetField: UseFormResetField<IItem>;
  errors: FieldErrors<IItem>;
  t: (key: string) => string;
  serviceItems: IServiceItem[] | undefined;
  isLoading: boolean;
  servicesLoadError: boolean;
  append: any;
}> = ({
  setServicesFields,
  servicesFields,
  control,
  setValue,
  getValues,
  removeField,
  errors,
  t,
  serviceItems,
  isLoading,
  servicesLoadError,
  append,
}) => {
  return (
    <>
      {isLoading ? (
        <Typography variant="body1">{t('loading...')}</Typography>
      ) : servicesLoadError ? (
        <Typography variant="body1" color="error">
          {t('loading_error')}
        </Typography>
      ) : serviceItems ? (
        <>
          <Box component="fieldset" className="ticket-container">
            <Typography component="legend" sx={{ px: 1, fontSize: 12, mb: 0 }}>
              {t('editTicket.service_catalog')}
              <Tooltip title={<>{t('editTicket.note_affect_final_payment')}</>}>
                <IconButton color="inherit">
                  <HelpOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Typography>

            <Link
              target="_blank"
              rel="noopener noreferrer"
              href="/service-catalog"
              className="ticket-label"
            >
              {t('editTicket.manage_services_fees')}
            </Link>

            <Grid
              container
              spacing={1}
              sx={{
                marginTop: '0 !important',
                marginBottom:
                  servicesFields.length > 0
                    ? '14px !important'
                    : '0 !important',
              }}
            >
              {serviceItems &&
                servicesFields.map((fieldId, index) => {
                  const currentService = serviceItems.find(
                    (s) =>
                      s.id === getValues(`servicesCatalog.${index}.service_id`),
                  );

                  return (
                    <>
                      <Grid
                        key={fieldId}
                        size={
                          currentService ? { xs: 8, sm: 8 } : { xs: 11, sm: 11 }
                        }
                      >
                        <Controller
                          control={control}
                          name={`servicesCatalog.${index}.service_id`}
                          rules={{
                            required: t('form.required'),
                          }}
                          render={({ field: controllerField }) => (
                            <Autocomplete
                              value={currentService || null}
                              size="small"
                              freeSolo={false}
                              options={serviceItems || []}
                              getOptionLabel={(option) => option.name}
                              renderOption={(props, option) => {
                                return <li {...props}>{option.name}</li>;
                              }}
                              onChange={(_, newValue) => {
                                controllerField.onChange(newValue?.id);

                                setValue(
                                  `servicesCatalog.${index}.price_at_use`,
                                  newValue?.price,
                                );

                                setValue(
                                  `servicesCatalog.${index}.type`,
                                  'catalog',
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  value={params.inputProps.value ?? ''}
                                  label={t('editTicket.select_service')}
                                  error={
                                    !!errors?.servicesCatalog?.[index]
                                      ?.service_id
                                  }
                                  helperText={
                                    errors?.servicesCatalog?.[index]?.service_id
                                      ?.message
                                  }
                                />
                              )}
                            />
                          )}
                        />
                      </Grid>
                      {currentService && (
                        <Grid size={{ xs: 3 }}>
                          <Controller
                            control={control}
                            defaultValue={currentService?.price}
                            name={`servicesCatalog.${index}.price_at_use`}
                            rules={{
                              required: t('form.required'),
                            }}
                            render={({ field }) => {
                              const inputValue = field.value ?? null;
                              return (
                                <TextField
                                  type="number"
                                  slotProps={{
                                    htmlInput: {
                                      min: 0,
                                      step: 0.01,
                                    },
                                  }}
                                  label={
                                    <Box>
                                      {t('form.price')}
                                      {inputValue && (
                                        <Tooltip
                                          title={
                                            <>
                                              {t(
                                                'service_catalog.columns.price',
                                              )}
                                              : {currentService?.price}
                                            </>
                                          }
                                        >
                                          <IconButton color="inherit">
                                            <HelpOutlineIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  }
                                  variant="outlined"
                                  size="small"
                                  rows={4}
                                  {...field}
                                  value={field.value ?? ''}
                                  error={
                                    !!errors?.servicesCatalog &&
                                    !!errors?.servicesCatalog[index]
                                      ?.price_at_use
                                  }
                                  helperText={
                                    !!errors?.servicesCatalog &&
                                    !!errors?.servicesCatalog[index]
                                      ?.price_at_use
                                      ? errors?.servicesCatalog?.[index]
                                          ?.price_at_use?.message
                                      : ''
                                  }
                                />
                              );
                            }}
                          />
                        </Grid>
                      )}
                      <Grid size={{ xs: 1, sm: 1 }}>
                        <IconButton
                          onClick={() => {
                            setServicesFields((prev) =>
                              prev.filter((item) => item !== fieldId),
                            );
                            removeField(index);
                          }}
                          sx={{
                            '&:hover': {
                              color: 'error.main',
                            },
                          }}
                        >
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </>
                  );
                })}
            </Grid>

            {serviceItems && serviceItems.length > 0 ? (
              <Box sx={{ textAlign: 'right', mb: 1 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    append({
                      type: 'catalog',
                      id: null,
                    });
                    setServicesFields((prev) => [...prev, uuidv4()]);
                  }}
                >
                  {t('form.btn_add')}
                </Button>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'right', mb: 1 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    window.open('/add-service', '_blank');
                  }}
                >
                  {t('editTicket.add_first_service_to_catalog')}
                </Button>
              </Box>
            )}
          </Box>
        </>
      ) : (
        <>додати першу послугу в каталог</>
      )}
    </>
  );
};
